import {
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
  useEffect,
  useState,
} from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { ApiError } from '@/lib/api';
import {
  type LookupRequest,
  type LookupResult,
  useDistributorLookup,
  useDistributorSettings,
} from '@/lib/distributors';
import { useCreateLocation, useLocations } from '@/lib/locations';
import {
  type PartCategory,
  type PartCreateInput,
  usePartCategories,
  useCreatePart,
} from '@/lib/parts';

interface NewPartModalProps {
  orgSlug: string | undefined;
  onClose: () => void;
  onCreated: (partId: number) => void;
}

const MOUNTING_OPTIONS = [
  { value: '', label: '— unset —' },
  { value: 'smd', label: 'SMD' },
  { value: 'tht', label: 'Through-hole (THT)' },
  { value: 'chassis', label: 'Chassis' },
  { value: 'other', label: 'Other' },
];

const LIFECYCLE_OPTIONS = [
  { value: 'unknown', label: 'Unknown' },
  { value: 'active', label: 'Active' },
  { value: 'nrnd', label: 'Not recommended for new design' },
  { value: 'obsolete', label: 'Obsolete' },
  { value: 'preview', label: 'Preview' },
];

const ROHS_OPTIONS = [
  { value: '', label: '— unknown —' },
  { value: 'true', label: 'RoHS compliant' },
  { value: 'false', label: 'Not compliant' },
];

/**
 * Per-category field relevance. The keys are category slugs; the values
 * are the set of "specialty" fields that *do* apply. A field NOT listed
 * in any of these sets is always shown; a field listed here is shown
 * only when the chosen category matches (or when "Show all fields" is on).
 *
 * Always-shown fields: value, tolerance, voltage_v, package, mounting,
 * RoHS, kicad_tracked, datasheet_url, notes. They're either useful
 * everywhere or harmless when blank.
 *
 * Specialty fields: dielectric (caps), vf (diodes/LEDs), vr (diodes),
 * power_w (resistors/transistors/regulators/switches/power), polarity
 * (BJT/FET/TVS), regulator output (voltage-regulator), voltage_clamp_v
 * (TVS), hold/trip currents (PTC fuses), color + wavelength_nm (LEDs),
 * pitch_mm (connectors / headers).
 */
// Lifted to `lib/field-relevance.ts` so the drawer and EditPartModal
// can apply the same rubric.
import { isSpecialtyRelevant } from '@/lib/field-relevance';

interface DuplicateInfo {
  existing_part_id: number;
  existing_part_number: string;
  message: string;
}

export function NewPartModal({ orgSlug, onClose, onCreated }: NewPartModalProps) {
  const create = useCreatePart(orgSlug);
  const lookup = useDistributorLookup(orgSlug);
  const categories = usePartCategories(orgSlug);
  const locations = useLocations(orgSlug);
  const createLocation = useCreateLocation(orgSlug);
  // Inventory + image hooks need the part_id so they're constructed
  // post-create. We grab the queued files / values here in form state
  // and apply them after `create.mutateAsync` returns.

  // Identity
  const [mfgPn, setMfgPn] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [altMfgPn, setAltMfgPn] = useState('');
  const [altManufacturer, setAltManufacturer] = useState('');
  const [description, setDescription] = useState('');

  // Classification
  const [categoryId, setCategoryId] = useState<string>('');
  const [value, setValue] = useState('');
  const [tolerance, setTolerance] = useState('');
  const [dielectric, setDielectric] = useState('');
  const [lifecycle, setLifecycle] = useState('unknown');

  // Physical
  const [packageStr, setPackage] = useState('');
  const [mounting, setMounting] = useState<string>('');

  // Electrical
  const [voltageV, setVoltageV] = useState('');
  const [powerW, setPowerW] = useState('');
  const [vf, setVf] = useState('');
  const [vr, setVr] = useState('');

  // Type-specific specs (migration 0013). For now these flow through
  // the form state from the distributor lookup → the saved Part, but
  // don't render dedicated form inputs yet — UI work for those is a
  // follow-up commit. The values still persist on save so a user who
  // pastes an LCSC URL gets correctly-categorized data even before
  // the matching inputs land.
  const [polarity, setPolarity] = useState('');
  const [outputVoltageV, setOutputVoltageV] = useState('');
  const [outputVoltageVMax, setOutputVoltageVMax] = useState('');
  const [outputCurrentA, setOutputCurrentA] = useState('');
  const [regulatorOutputType, setRegulatorOutputType] = useState('');
  const [voltageClampV, setVoltageClampV] = useState('');
  const [holdCurrentA, setHoldCurrentA] = useState('');
  const [tripCurrentA, setTripCurrentA] = useState('');
  const [color, setColor] = useState('');
  const [wavelengthNm, setWavelengthNm] = useState('');
  const [pitchMm, setPitchMm] = useState('');
  // 0014 spec columns.
  const [luminousIntensityMcd, setLuminousIntensityMcd] = useState('');
  const [loadCapacitancePf, setLoadCapacitancePf] = useState('');
  const [rdsOnOhms, setRdsOnOhms] = useState('');
  const [hfe, setHfe] = useState('');

  // Sourcing / metadata
  const [rohs, setRohs] = useState<string>('');
  const [datasheetUrl, setDatasheetUrl] = useState('');
  const [notes, setNotes] = useState('');

  // KiCad library linkage. Stored as KiCad's LIB_ID format
  // ("library:symbol_name"), which is what KiCad's BOM exports use too.
  // Populated by the user manually for now; eventually a KiCad plugin
  // will sync these on its own. A part is considered "linked" when
  // either field is set; "fully linked" when both are.
  const [kicadSymbol, setKicadSymbol] = useState('');
  const [kicadFootprint, setKicadFootprint] = useState('');
  const [kicadHas3dModel, setKicadHas3dModel] = useState(false);

  // Inventory section
  const [stockQty, setStockQty] = useState('');
  const [stockApproximate, setStockApproximate] = useState(false);
  const [stockThreshold, setStockThreshold] = useState('');
  const [locationId, setLocationId] = useState<string>(''); // '' = no location, 'new' = new-location prompt
  const [newLocationName, setNewLocationName] = useState('');

  // Images: queued File objects + their object-URLs for previewing.
  // Uploaded sequentially after the part is created so we have an id.
  const [images, setImages] = useState<File[]>([]);
  const [imageError, setImageError] = useState<string | null>(null);
  const [postCreatePhase, setPostCreatePhase] = useState<
    'idle' | 'inventory' | 'images' | 'done'
  >('idle');

  // Once the part is created we keep its id around so the user can
  // dismiss the modal cleanly when an image upload partial-failed.
  const [createdPartId, setCreatedPartId] = useState<number | null>(null);

  // Set when the lookup matched an existing part (server returned
  // `part_created: false`). The modal shows a brief confirmation
  // panel so the user understands they updated an existing row, then
  // a button takes them to that part. Without this, "Use this data"
  // on a re-paste would silently close the modal — confusing UX.
  const [updatedExisting, setUpdatedExisting] = useState<{
    partId: number;
    partNumber: string;
  } | null>(null);

  const [duplicate, setDuplicate] = useState<DuplicateInfo | null>(null);
  // Submission errors that aren't part of the duplicate-detection
  // 409 path. Cleared on each new submit attempt + when the user
  // edits the form. Without this, lookup-driven failures (vendor
  // commit returning an unexpected shape, etc.) silently failed
  // because the create.error renderer below only catches errors
  // from the create mutation, not lookup.mutateAsync.
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showAllFields, setShowAllFields] = useState(false);

  // When the user clicks "Use this data" in the distributor-lookup
  // banner, we copy the DTO fields into the form state above and stash
  // `(slug, pn, distributorName)` here so the bottom Save button knows
  // to route the create through the lookup endpoint (which also adds
  // the supplier row + schedules image fetch). Set null to clear back
  // to "manual entry" mode.
  const [lookupSource, setLookupSource] = useState<{
    slug: string;
    pn: string;
    distributorName: string;
  } | null>(null);

  const applyLookup = (result: LookupResult) => {
    if (result.mpn) setMfgPn(result.mpn);
    if (result.manufacturer) setManufacturer(result.manufacturer);
    if (result.description) setDescription(result.description);
    if (result.package) setPackage(result.package);
    if (result.datasheet_url) setDatasheetUrl(result.datasheet_url);
    if (result.lifecycle) setLifecycle(result.lifecycle);
    if (result.rohs != null) setRohs(result.rohs ? 'true' : 'false');
    if (result.category_id != null) setCategoryId(String(result.category_id));
    // Structured specs parsed from the upstream's parameter list.
    if (result.value) setValue(result.value);
    if (result.tolerance) setTolerance(result.tolerance);
    if (result.dielectric) setDielectric(result.dielectric);
    if (result.voltage_v != null) setVoltageV(String(result.voltage_v));
    if (result.power_w != null) setPowerW(String(result.power_w));
    if (result.vf != null) setVf(String(result.vf));
    if (result.vr != null) setVr(String(result.vr));
    if (result.mounting) setMounting(result.mounting);
    // Type-specific specs (migration 0013). Each only applies to a
    // subset of part categories; the adapter populates whichever match
    // and leaves the rest null.
    if (result.polarity) setPolarity(result.polarity);
    if (result.output_voltage_v != null)
      setOutputVoltageV(String(result.output_voltage_v));
    if (result.output_voltage_v_max != null)
      setOutputVoltageVMax(String(result.output_voltage_v_max));
    if (result.output_current_a != null)
      setOutputCurrentA(String(result.output_current_a));
    if (result.regulator_output_type)
      setRegulatorOutputType(result.regulator_output_type);
    if (result.voltage_clamp_v != null)
      setVoltageClampV(String(result.voltage_clamp_v));
    if (result.hold_current_a != null)
      setHoldCurrentA(String(result.hold_current_a));
    if (result.trip_current_a != null)
      setTripCurrentA(String(result.trip_current_a));
    if (result.color) setColor(result.color);
    if (result.wavelength_nm != null)
      setWavelengthNm(String(result.wavelength_nm));
    if (result.pitch_mm != null) setPitchMm(String(result.pitch_mm));
    if (result.luminous_intensity_mcd != null)
      setLuminousIntensityMcd(String(result.luminous_intensity_mcd));
    if (result.load_capacitance_pf != null)
      setLoadCapacitancePf(String(result.load_capacitance_pf));
    if (result.rds_on_ohms != null)
      setRdsOnOhms(String(result.rds_on_ohms));
    if (result.hfe != null) setHfe(String(result.hfe));
    // Try to map the LCSC `package_form` to one of our mounting enum
    // values when sensible. SMT-style packages (0603, SOIC, QFN…) are
    // surface-mount; through-hole packages would need different
    // pattern-matching that LCSC doesn't directly tell us. For now we
    // leave mounting unset and let the user pick.
    setLookupSource({
      slug: result.distributor_slug,
      pn: result.distributor_pn,
      distributorName:
        result.distributor_name ?? result.distributor_slug.toUpperCase(),
    });
  };

  const clearLookup = () => setLookupSource(null);

  // The dropdown stores ID; we need slug to drive field-relevance lookups.
  const selectedCategory = categories.data?.find(
    (c) => String(c.id) === categoryId,
  );
  const selectedSlug = selectedCategory?.slug ?? null;
  const showDielectric = isSpecialtyRelevant('dielectric', selectedSlug, showAllFields);
  const showVf = isSpecialtyRelevant('vf', selectedSlug, showAllFields);
  const showVr = isSpecialtyRelevant('vr', selectedSlug, showAllFields);
  const showPowerW = isSpecialtyRelevant('power_w', selectedSlug, showAllFields);
  const showPolarity = isSpecialtyRelevant('polarity', selectedSlug, showAllFields);
  const showRegulatorOutput = isSpecialtyRelevant(
    'regulator_output', selectedSlug, showAllFields,
  );
  const showVoltageClamp = isSpecialtyRelevant(
    'voltage_clamp', selectedSlug, showAllFields,
  );
  const showFuseCurrents = isSpecialtyRelevant(
    'fuse_currents', selectedSlug, showAllFields,
  );
  const showLedColor = isSpecialtyRelevant('led_color', selectedSlug, showAllFields);
  const showPitch = isSpecialtyRelevant('pitch', selectedSlug, showAllFields);
  const showLuminous = isSpecialtyRelevant(
    'luminous_intensity', selectedSlug, showAllFields,
  );
  const showLoadCap = isSpecialtyRelevant(
    'load_capacitance', selectedSlug, showAllFields,
  );
  const showRdsOn = isSpecialtyRelevant('rds_on', selectedSlug, showAllFields);
  const showHfe = isSpecialtyRelevant('hfe', selectedSlug, showAllFields);

  const numOrUndef = (raw: string): number | undefined =>
    raw.trim() === '' ? undefined : Number(raw);

  const buildPayload = (): PartCreateInput => ({
    mfg_pn: mfgPn.trim() || undefined,
    manufacturer: manufacturer.trim() || undefined,
    alt_mfg_pn: altMfgPn.trim() || undefined,
    alt_manufacturer: altManufacturer.trim() || undefined,
    description: description.trim() || undefined,
    category_id: categoryId === '' ? undefined : Number(categoryId),
    package: packageStr.trim() || undefined,
    mounting: mounting || undefined,
    value: value.trim() || undefined,
    tolerance: tolerance.trim() || undefined,
    dielectric: dielectric.trim() || undefined,
    voltage_v: numOrUndef(voltageV),
    power_w: numOrUndef(powerW),
    vf: numOrUndef(vf),
    vr: numOrUndef(vr),
    polarity: polarity.trim() || undefined,
    output_voltage_v: numOrUndef(outputVoltageV),
    output_voltage_v_max: numOrUndef(outputVoltageVMax),
    output_current_a: numOrUndef(outputCurrentA),
    regulator_output_type: regulatorOutputType.trim() || undefined,
    voltage_clamp_v: numOrUndef(voltageClampV),
    hold_current_a: numOrUndef(holdCurrentA),
    trip_current_a: numOrUndef(tripCurrentA),
    color: color.trim() || undefined,
    wavelength_nm: numOrUndef(wavelengthNm),
    pitch_mm: numOrUndef(pitchMm),
    luminous_intensity_mcd: numOrUndef(luminousIntensityMcd),
    load_capacitance_pf: numOrUndef(loadCapacitancePf),
    rds_on_ohms: numOrUndef(rdsOnOhms),
    hfe: numOrUndef(hfe),
    rohs_compliant: rohs === '' ? undefined : rohs === 'true',
    kicad_symbol: kicadSymbol.trim() || undefined,
    kicad_footprint: kicadFootprint.trim() || undefined,
    kicad_has_3d_model: kicadHas3dModel || undefined,
    // Derived: a part is "tracked" once it has any KiCad library reference.
    // Keeps the legacy boolean useful for parts imported from the original
    // spreadsheet via the "x" marker.
    kicad_tracked:
      Boolean(kicadSymbol.trim()) || Boolean(kicadFootprint.trim()) || undefined,
    lifecycle,
    datasheet_url: datasheetUrl.trim() || undefined,
    notes: notes.trim() || undefined,
  });

  // Post-create: apply inventory and upload images. We do these against
  // the freshly-created part_id (the create endpoint already auto-creates
  // the inventory row, so we PATCH rather than POST). Failures here are
  // surfaced but don't roll back the part — the user can re-enter
  // inventory or re-upload images from the drawer.
  const applyInventoryAndImages = async (
    partId: number,
    locId: number | null,
  ): Promise<void> => {
    const qty = stockQty.trim();
    const threshold = stockThreshold.trim();
    const inventoryDirty =
      qty !== '' || threshold !== '' || stockApproximate || locId !== null;
    if (inventoryDirty) {
      setPostCreatePhase('inventory');
      const inventoryUrl = `/api/orgs/${orgSlug}/parts/${partId}/inventory`;
      const body = JSON.stringify({
        quantity_on_hand: qty === '' ? undefined : Math.max(0, Number(qty) || 0),
        quantity_threshold:
          threshold === '' ? undefined : Math.max(0, Number(threshold) || 0),
        quantity_is_approximate: stockApproximate || undefined,
        location_id: locId,
      });
      const response = await fetch(inventoryUrl, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body,
      });
      if (!response.ok) {
        // Don't abort — log to console and continue. Stock can be set
        // from the drawer if this somehow fails.
        // eslint-disable-next-line no-console.warn('inventory patch failed', await response.text());
      }
    }
    if (images.length > 0) {
      setPostCreatePhase('images');
      for (const file of images) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await fetch(
          `/api/orgs/${orgSlug}/parts/${partId}/attachments`,
          { method: 'POST', credentials: 'include', body: formData },
        );
        if (!response.ok) {
          let detail = `HTTP ${response.status}`;
          try {
            const body = await response.json();
            if (body && typeof body === 'object' && 'detail' in body) {
              detail = String((body as { detail: unknown }).detail);
            }
          } catch {
            /* keep the generic detail */
          }
          setImageError(`${file.name}: ${detail}`);
          // Continue with the rest so a single bad file doesn't block
          // the others.
        }
      }
    }
    setPostCreatePhase('done');
  };

  const submit = async (allowDuplicate: boolean) => {
    setDuplicate(null);
    setImageError(null);
    setSubmitError(null);
    try {
      // Resolve the location id BEFORE creating the part so a typed-in
      // new-location name lands as a real Location row first. If the
      // creation fails, the part create still hasn't started and the
      // user can fix the name and retry.
      let locIdNum: number | null = null;
      if (locationId === 'new' && newLocationName.trim()) {
        const created = await createLocation.mutateAsync({
          name: newLocationName.trim(),
        });
        locIdNum = created.id;
      } else if (locationId !== '' && locationId !== 'new') {
        locIdNum = Number(locationId);
      }

      let partId: number;
      let updatedPartNumber: string | null = null;
      if (lookupSource) {
        // Lookup-loaded path: send the form values as `part_overrides`
        // alongside `(slug, pn)`. Server re-runs the adapter and creates
        // Part + PartSupplier in one transaction. allowDuplicate is
        // ignored on this path because the lookup endpoint has its own
        // idempotency rules (same-PN-twice returns the existing part).
        const payload = buildPayload();
        const lookupRequest: LookupRequest = {
          distributor_slug: lookupSource.slug,
          pn: lookupSource.pn,
          part_overrides: payload as unknown as Record<string, unknown>,
        };
        const result = await lookup.mutateAsync(lookupRequest);
        if (result.part_id == null) {
          // Shouldn't happen on the non-preview path. Surface a
          // diagnostic so the user knows the click failed instead of
          // silently doing nothing — this used to swallow the case
          // where the server returned an HTTP 200 with an unexpected
          // shape (idempotency mismatch, etc.).
          setSubmitError(
            'The vendor lookup completed, but the server didn\'t '
            + 'return a part id. Try refreshing and re-pasting the URL.',
          );
          return;
        }
        partId = result.part_id;
        // When the server matched an existing part instead of creating,
        // surface a brief in-modal confirmation so the user knows their
        // paste hit an existing row rather than silently closing.
        if (!result.part_created) {
          updatedPartNumber = result.part_number;
        }
      } else {
        const part = await create.mutateAsync({
          payload: buildPayload(),
          allowDuplicate,
        });
        partId = part.id;
      }

      await applyInventoryAndImages(partId, locIdNum);
      // If an image failed to upload, hold the modal open so the user
      // sees the error. They can close manually; the part is already
      // saved and the drawer will let them retry the upload.
      if (imageError) {
        setCreatedPartId(partId);
        return;
      }
      // When the lookup matched an existing part, hold the modal
      // briefly with a green "updated AR10042" confirmation before
      // navigating, so the user understands their paste hit an
      // existing row.
      if (updatedPartNumber !== null) {
        setUpdatedExisting({ partId, partNumber: updatedPartNumber });
        return;
      }
      onCreated(partId);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const detail = (err.body as { detail?: unknown })?.detail;
        if (
          detail &&
          typeof detail === 'object' &&
          'existing_part_id' in (detail as object)
        ) {
          setDuplicate(detail as DuplicateInfo);
          return;
        }
      }
      // Don't swallow other errors — without surfacing them the user
      // sees the progress bar finish and nothing happens, with no clue
      // what went wrong (especially for vendor-lookup failures, where
      // create.error never populates because we used lookup.mutateAsync
      // instead). Pull the friendliest message we can from the error.
      const message = err instanceof Error ? err.message : String(err);
      setSubmitError(message);
    }
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void submit(false);
  };

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-stone-900/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 shadow-xl">
        <div className="flex items-center justify-between border-b border-stone-200 dark:border-stone-800 px-5 py-3">
          <h2 className="text-lg font-semibold">New part</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-stone-500 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <form
          onSubmit={onSubmit}
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
        >
          <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
            <DistributorLookupBanner
              orgSlug={orgSlug}
              lookupSource={lookupSource}
              onApply={applyLookup}
              onClear={clearLookup}
            />

            <p className="text-xs text-stone-500 dark:text-stone-400">
              Part number is auto-assigned from your org's prefix on save. All
              fields are optional, but mfg PN + description make a part findable.
              Suppliers and inventory are added after creation.
            </p>

            <Section title="Identity">
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Manufacturer PN" mono>
                  <Input
                    value={mfgPn}
                    onChange={(e) => setMfgPn(e.target.value)}
                    placeholder="GRM188R71H104KA93D"
                  />
                </Field>
                <Field label="Manufacturer">
                  <Input
                    value={manufacturer}
                    onChange={(e) => setManufacturer(e.target.value)}
                    placeholder="Murata"
                  />
                </Field>
                <Field label="Alt Manufacturer PN" mono>
                  <Input
                    value={altMfgPn}
                    onChange={(e) => setAltMfgPn(e.target.value)}
                  />
                </Field>
                <Field label="Alt Manufacturer">
                  <Input
                    value={altManufacturer}
                    onChange={(e) => setAltManufacturer(e.target.value)}
                  />
                </Field>
                <Field label="Description" span={2}>
                  <Input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="CAP CER 0.1UF 50V X7R 0603"
                  />
                </Field>
              </div>
            </Section>

            <Section title="Classification">
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Category">
                  <select
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                    className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm"
                  >
                    <option value="">— uncategorized —</option>
                    {renderCategoryOptions(categories.data)}
                  </select>
                </Field>
                <Field label="Lifecycle">
                  <select
                    value={lifecycle}
                    onChange={(e) => setLifecycle(e.target.value)}
                    className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm"
                  >
                    {LIFECYCLE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Value">
                  <Input
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder="10kΩ, 0.1µF, …"
                  />
                </Field>
                <Field label="Tolerance">
                  <Input
                    value={tolerance}
                    onChange={(e) => setTolerance(e.target.value)}
                    placeholder="±5%"
                  />
                </Field>
                {showDielectric && (
                  <Field label="Dielectric (caps)">
                    <Input
                      value={dielectric}
                      onChange={(e) => setDielectric(e.target.value)}
                      placeholder="C0G, X7R, …"
                    />
                  </Field>
                )}
                {showPolarity && (
                  <Field label="Polarity / Type">
                    <Input
                      value={polarity}
                      onChange={(e) => setPolarity(e.target.value)}
                      placeholder="NPN, N-Channel, Unidirectional, …"
                    />
                  </Field>
                )}
                {showLedColor && (
                  <>
                    <Field label="Color">
                      <Input
                        value={color}
                        onChange={(e) => setColor(e.target.value)}
                        placeholder="Red, Blue, Emerald Green, …"
                      />
                    </Field>
                    <Field label="Wavelength (nm)">
                      <Input
                        type="number"
                        step="any"
                        value={wavelengthNm}
                        onChange={(e) => setWavelengthNm(e.target.value)}
                        placeholder="525"
                      />
                    </Field>
                  </>
                )}
                {showLuminous && (
                  <Field label="Luminous intensity (mcd)">
                    <Input
                      type="number"
                      step="any"
                      value={luminousIntensityMcd}
                      onChange={(e) => setLuminousIntensityMcd(e.target.value)}
                      placeholder="300"
                    />
                  </Field>
                )}
              </div>
            </Section>

            <Section title="Physical">
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Package">
                  <Input
                    value={packageStr}
                    onChange={(e) => setPackage(e.target.value)}
                    placeholder="0603, SOIC-8, …"
                  />
                </Field>
                <Field label="Mounting">
                  <select
                    value={mounting}
                    onChange={(e) => setMounting(e.target.value)}
                    className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm"
                  >
                    {MOUNTING_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </Field>
                {showPitch && (
                  <Field label="Pitch (mm)">
                    <Input
                      type="number"
                      step="any"
                      value={pitchMm}
                      onChange={(e) => setPitchMm(e.target.value)}
                      placeholder="2.54"
                    />
                  </Field>
                )}
              </div>
            </Section>

            <Section
              title="Electrical"
              action={
                <label className="flex cursor-pointer items-center gap-1.5 text-xs font-normal text-stone-500 dark:text-stone-400">
                  <input
                    type="checkbox"
                    checked={showAllFields}
                    onChange={(e) => setShowAllFields(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border border-stone-300 dark:border-stone-700 text-forge-600 focus:ring-forge-500"
                  />
                  Show all fields
                </label>
              }
            >
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Voltage (V)">
                  <Input
                    type="number"
                    step="any"
                    value={voltageV}
                    onChange={(e) => setVoltageV(e.target.value)}
                    placeholder="50"
                  />
                </Field>
                {showPowerW && (
                  <Field label="Power (W)">
                    <Input
                      type="number"
                      step="any"
                      value={powerW}
                      onChange={(e) => setPowerW(e.target.value)}
                      placeholder="0.1"
                    />
                  </Field>
                )}
                {showVf && (
                  <Field label="Vf (forward voltage)">
                    <Input
                      type="number"
                      step="any"
                      value={vf}
                      onChange={(e) => setVf(e.target.value)}
                      placeholder="2.1"
                    />
                  </Field>
                )}
                {showVr && (
                  <Field label="Vr (reverse voltage)">
                    <Input
                      type="number"
                      step="any"
                      value={vr}
                      onChange={(e) => setVr(e.target.value)}
                      placeholder="100"
                    />
                  </Field>
                )}
                {showVoltageClamp && (
                  <Field label="Vc clamp (V)">
                    <Input
                      type="number"
                      step="any"
                      value={voltageClampV}
                      onChange={(e) => setVoltageClampV(e.target.value)}
                      placeholder="9.2"
                    />
                  </Field>
                )}
                {showLoadCap && (
                  <Field label="Load capacitance (pF)">
                    <Input
                      type="number"
                      step="any"
                      value={loadCapacitancePf}
                      onChange={(e) => setLoadCapacitancePf(e.target.value)}
                      placeholder="20"
                    />
                  </Field>
                )}
                {showRdsOn && (
                  <Field label="RDS(on) (Ω)">
                    <Input
                      type="number"
                      step="any"
                      value={rdsOnOhms}
                      onChange={(e) => setRdsOnOhms(e.target.value)}
                      placeholder="0.05"
                    />
                  </Field>
                )}
                {showHfe && (
                  <Field label="hFE (DC current gain)">
                    <Input
                      type="number"
                      step="any"
                      value={hfe}
                      onChange={(e) => setHfe(e.target.value)}
                      placeholder="300"
                    />
                  </Field>
                )}
              </div>
              {!showAllFields && selectedSlug && (
                <p className="mt-2 text-xs text-stone-500 dark:text-stone-400">
                  Hidden fields don't apply to {selectedCategory?.name ?? 'this category'}.
                  Enable "Show all fields" if your part is unusual.
                </p>
              )}
            </Section>

            {showRegulatorOutput && (
              <Section title="Regulator output">
                <div className="grid gap-3 md:grid-cols-2">
                  <Field label="Output voltage (V)">
                    <Input
                      type="number"
                      step="any"
                      value={outputVoltageV}
                      onChange={(e) => setOutputVoltageV(e.target.value)}
                      placeholder="3.3"
                    />
                  </Field>
                  <Field label="Output voltage max (V)">
                    <Input
                      type="number"
                      step="any"
                      value={outputVoltageVMax}
                      onChange={(e) => setOutputVoltageVMax(e.target.value)}
                      placeholder="20"
                    />
                  </Field>
                  <Field label="Output current (A)">
                    <Input
                      type="number"
                      step="any"
                      value={outputCurrentA}
                      onChange={(e) => setOutputCurrentA(e.target.value)}
                      placeholder="1"
                    />
                  </Field>
                  <Field label="Type">
                    <Input
                      value={regulatorOutputType}
                      onChange={(e) =>
                        setRegulatorOutputType(e.target.value)
                      }
                      placeholder="Fixed, Adjustable, Programmable"
                    />
                  </Field>
                </div>
                <p className="mt-2 text-xs text-stone-500 dark:text-stone-400">
                  Min/max are the bounds of an adjustable output range; leave
                  max blank for fixed parts.
                </p>
              </Section>
            )}

            {showFuseCurrents && (
              <Section title="Fuse currents">
                <div className="grid gap-3 md:grid-cols-2">
                  <Field label="Hold current (A)">
                    <Input
                      type="number"
                      step="any"
                      value={holdCurrentA}
                      onChange={(e) => setHoldCurrentA(e.target.value)}
                      placeholder="0.5"
                    />
                  </Field>
                  <Field label="Trip current (A)">
                    <Input
                      type="number"
                      step="any"
                      value={tripCurrentA}
                      onChange={(e) => setTripCurrentA(e.target.value)}
                      placeholder="1"
                    />
                  </Field>
                </div>
              </Section>
            )}

            <Section title="Sourcing & metadata">
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="RoHS">
                  <select
                    value={rohs}
                    onChange={(e) => setRohs(e.target.value)}
                    className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm"
                  >
                    {ROHS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Datasheet URL" span={2}>
                  <Input
                    type="url"
                    value={datasheetUrl}
                    onChange={(e) => setDatasheetUrl(e.target.value)}
                    placeholder="https://…"
                  />
                </Field>
              </div>
            </Section>

            <Section title="Stock & location">
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Quantity on hand">
                  <Input
                    type="number"
                    min={0}
                    value={stockQty}
                    onChange={(e) => setStockQty(e.target.value)}
                    placeholder="0"
                  />
                </Field>
                <Field label="Low-stock threshold">
                  <Input
                    type="number"
                    min={0}
                    value={stockThreshold}
                    onChange={(e) => setStockThreshold(e.target.value)}
                    placeholder="0"
                  />
                </Field>
                <Field label="Location" span={2}>
                  <select
                    value={locationId}
                    onChange={(e) => setLocationId(e.target.value)}
                    className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm"
                  >
                    <option value="">— no location —</option>
                    {(locations.data ?? []).map((loc) => (
                      <option key={loc.id} value={String(loc.id)}>
                        {loc.name}
                      </option>
                    ))}
                    <option value="new">+ New location…</option>
                  </select>
                  {locationId === 'new' && (
                    <Input
                      value={newLocationName}
                      onChange={(e) => setNewLocationName(e.target.value)}
                      placeholder="Bench Reel A"
                      className="mt-2"
                    />
                  )}
                </Field>
              </div>
              <label className="mt-2 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={stockApproximate}
                  onChange={(e) => setStockApproximate(e.target.checked)}
                  className="h-4 w-4 rounded border border-stone-300 dark:border-stone-700 text-forge-600 focus:ring-forge-500"
                />
                Quantity is approximate (e.g. "3500+")
              </label>
            </Section>

            <Section title="Images">
              <ImagePickerSection
                images={images}
                onChange={setImages}
                phase={postCreatePhase}
                error={imageError}
              />
            </Section>

            <Section title="KiCad library">
              <p className="mb-2 text-xs text-stone-500 dark:text-stone-400">
                Optional but powerful. Use KiCad's <span className="font-mono">library:name</span>{' '}
                format. A part is "fully defined" once it has a symbol, a
                footprint, and that footprint references a 3D model.
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Symbol (LIB_ID)" mono>
                  <Input
                    value={kicadSymbol}
                    onChange={(e) => setKicadSymbol(e.target.value)}
                    placeholder="AMR Diode:AR10042"
                  />
                </Field>
                <Field label="Footprint (LIB_ID)" mono>
                  <Input
                    value={kicadFootprint}
                    onChange={(e) => setKicadFootprint(e.target.value)}
                    placeholder="AMR_Footprints:R_0603_AR"
                  />
                </Field>
              </div>
              <label className="mt-3 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={kicadHas3dModel}
                  onChange={(e) => setKicadHas3dModel(e.target.checked)}
                  className="h-4 w-4 rounded border border-stone-300 dark:border-stone-700 text-forge-600 focus:ring-forge-500"
                />
                Footprint has a 3D model attached
              </label>
              <p className="mt-2 text-xs text-stone-500 dark:text-stone-400">
                {kicadStatusLabel(
                  kicadSymbol.trim(),
                  kicadFootprint.trim(),
                  kicadHas3dModel,
                )}
              </p>
            </Section>

            <Section title="Notes">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm focus:border-forge-500 focus:outline-none focus:ring-1 focus:ring-forge-500"
              />
            </Section>

            {duplicate && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                <p className="font-medium">
                  That mfg PN already exists as{' '}
                  <span className="font-mono">{duplicate.existing_part_number}</span>.
                </p>
                <p className="text-xs">
                  Adding it again is allowed (different lot, different revision,
                  or a duplicate to clean up later) but uncommon.
                </p>
                <div className="mt-2 flex gap-2">
                  <Button variant="secondary" onClick={() => setDuplicate(null)}>
                    Cancel
                  </Button>
                  <Button onClick={() => void submit(true)} disabled={create.isPending}>
                    Create anyway
                  </Button>
                </div>
              </div>
            )}

            {!duplicate && create.isError && (
              <p className="text-sm text-red-600">{(create.error as Error).message}</p>
            )}
            {!duplicate && lookup.isError && (
              <p className="text-sm text-red-600">{(lookup.error as Error).message}</p>
            )}
            {submitError && (
              <p className="text-sm text-red-600">{submitError}</p>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900 px-5 py-3">
            {updatedExisting !== null ? (
              <>
                <p className="mr-auto text-xs text-emerald-700 dark:text-emerald-400">
                  ✓ Updated existing part{' '}
                  <span className="font-mono">
                    {updatedExisting.partNumber}
                  </span>
                  . Any blank fields were filled from LCSC.
                </p>
                <Button
                  type="button"
                  onClick={() => onCreated(updatedExisting.partId)}
                >
                  Open part
                </Button>
              </>
            ) : createdPartId !== null ? (
              <>
                <p className="mr-auto text-xs text-stone-500 dark:text-stone-400">
                  Part saved. Some images didn't upload — you can retry from
                  the part drawer.
                </p>
                <Button type="button" onClick={() => onCreated(createdPartId)}>
                  Done
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" type="button" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    create.isPending ||
                    lookup.isPending ||
                    postCreatePhase !== 'idle'
                  }
                >
                  {create.isPending || lookup.isPending ? (
                    <>
                      <Spinner className="mr-2 h-4 w-4" />
                      Creating…
                    </>
                  ) : postCreatePhase === 'inventory' ? (
                    <>
                      <Spinner className="mr-2 h-4 w-4" />
                      Saving stock…
                    </>
                  ) : postCreatePhase === 'images' ? (
                    <>
                      <Spinner className="mr-2 h-4 w-4" />
                      Uploading images…
                    </>
                  ) : (
                    'Create part'
                  )}
                </Button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

function Section({
  title,
  children,
  action,
}: {
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400">
          {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function Field({
  label,
  children,
  span,
  mono,
}: {
  label: string;
  children: React.ReactNode;
  span?: 2;
  mono?: boolean;
}) {
  return (
    <label className={span === 2 ? 'md:col-span-2 block' : 'block'}>
      <span className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">{label}</span>
      <div className={mono ? 'font-mono' : ''}>{children}</div>
    </label>
  );
}

function ImagePickerSection({
  images,
  onChange,
  phase,
  error,
}: {
  images: File[];
  onChange: (next: File[]) => void;
  phase: 'idle' | 'inventory' | 'images' | 'done';
  error: string | null;
}) {
  // Object URLs are kept in sync with the queued File list so we can show
  // thumbnails in-modal before the part exists. Revoked on unmount and
  // when the file list changes — leaks would be tiny per modal session
  // but the cleanup is cheap and makes the lifecycle explicit.
  const [previews, setPreviews] = useState<string[]>([]);
  useEffect(() => {
    const urls = images.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => {
      for (const url of urls) URL.revokeObjectURL(url);
    };
  }, [images]);

  const add = (event: ChangeEvent<HTMLInputElement>) => {
    const list = event.target.files;
    if (!list || list.length === 0) return;
    const next = [...images, ...Array.from(list)].slice(0, 10);
    onChange(next);
    event.target.value = '';
  };
  const removeAt = (idx: number) => {
    onChange(images.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-2">
      <p className="text-xs text-stone-500 dark:text-stone-400">
        JPG, PNG, GIF, or WebP. Up to 5 MB each, 10 per part. They upload
        once the part is created.
      </p>

      <label className="inline-flex cursor-pointer items-center rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-1.5 text-sm font-medium text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-800">
        Add image
        <input
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          multiple
          onChange={add}
          className="hidden"
        />
      </label>

      {images.length > 0 && (
        <div className="grid grid-cols-4 gap-2">
          {images.map((file, idx) => (
            <div
              key={`${file.name}-${idx}`}
              className="relative aspect-square overflow-hidden rounded-md border border-stone-200 dark:border-stone-800"
            >
              <img
                src={previews[idx]}
                alt={file.name}
                className="h-full w-full object-cover"
              />
              <button
                type="button"
                onClick={() => removeAt(idx)}
                className="absolute right-1 top-1 rounded-full bg-white/90 px-1.5 py-0.5 text-xs text-red-600 shadow dark:bg-stone-900/90"
                aria-label="Remove queued image"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {phase === 'images' && (
        <p className="text-xs text-stone-500 dark:text-stone-400">Uploading images…</p>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

function kicadStatusLabel(
  symbol: string,
  footprint: string,
  has3dModel: boolean,
): string {
  if (!symbol && !footprint) {
    return "Not linked yet. Leave blank if the symbol library doesn't exist yet.";
  }
  if (!symbol || !footprint) {
    return 'Partial link — set both for the full BOM round-trip.';
  }
  if (has3dModel) {
    return '✓ Fully defined — symbol, footprint, and 3D model all linked.';
  }
  return 'Linked — symbol and footprint set. Add a 3D model to mark "fully defined".';
}


/**
 * Renders the category dropdown options as a clean two-level tree:
 * top-level rows that have children become <optgroup> with a "(general)"
 * option pinned at the top of the group; top-level rows without
 * children render as plain <option>. Sort order is the seeded
 * `sort_order` then alphabetical.
 */
function renderCategoryOptions(categories: PartCategory[] | undefined) {
  if (!categories) return null;
  const childrenByParent = new Map<number, PartCategory[]>();
  const roots: PartCategory[] = [];
  for (const c of categories) {
    if (c.parent_id === null) {
      roots.push(c);
    } else {
      const list = childrenByParent.get(c.parent_id) ?? [];
      list.push(c);
      childrenByParent.set(c.parent_id, list);
    }
  }
  const orderRoots = (a: PartCategory, b: PartCategory) =>
    a.sort_order - b.sort_order || a.name.localeCompare(b.name);
  roots.sort(orderRoots);
  for (const list of childrenByParent.values()) list.sort(orderRoots);

  return roots.map((root) => {
    const children = childrenByParent.get(root.id);
    if (!children || children.length === 0) {
      return (
        <option key={root.id} value={String(root.id)}>
          {root.name}
        </option>
      );
    }
    return (
      <optgroup key={root.id} label={root.name}>
        <option value={String(root.id)}>{root.name} (general)</option>
        {children.map((c) => (
          <option key={c.id} value={String(c.id)}>
            {c.name}
          </option>
        ))}
      </optgroup>
    );
  });
}

/**
 * Top-of-modal panel: paste a distributor URL or pick a vendor + PN.
 *
 * Two-step flow:
 *   1. Lookup (preview) — fetch the data, render a card showing every
 *      field PartForge would copy onto the new part. No DB writes.
 *   2. Confirm — call lookup again without `preview`, which actually
 *      creates the Part + PartSupplier row, schedules the lazy image
 *      and PDF fetch, and dismisses the modal directly to the new
 *      part's drawer.
 *
 * Lets the user back out (e.g., wrong URL, wrong manufacturer pulled,
 * doesn't recognize the part) without leaving an empty stub behind.
 *
 * Note: this panel uses plain `<div>` containers, NOT a nested `<form>`.
 * Nested forms are invalid HTML and the browser collapses them; the
 * inner submit button ends up firing the OUTER NewPartModal form,
 * which previously created an empty AR-numbered part on every paste.
 */
function DistributorLookupBanner({
  orgSlug,
  lookupSource,
  onApply,
  onClear,
}: {
  orgSlug: string | undefined;
  lookupSource: { slug: string; pn: string; distributorName: string } | null;
  onApply: (result: LookupResult) => void;
  onClear: () => void;
}) {
  const settings = useDistributorSettings(orgSlug);
  const lookup = useDistributorLookup(orgSlug);

  const [dismissed, setDismissed] = useState(false);
  const [input, setInput] = useState('');
  const [bareSlug, setBareSlug] = useState('lcsc');
  const [preview, setPreview] = useState<LookupResult | null>(null);

  // When a lookup is loaded into the form, the banner collapses to a
  // compact "Pulled from X — Clear" pill. Save lives at the bottom of
  // the modal as usual.
  if (lookupSource) {
    return (
      <div className="flex items-center justify-between rounded-md border border-emerald-200 dark:border-emerald-900/60 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2 text-sm">
        <div className="flex items-center gap-2">
          <span aria-hidden className="text-emerald-700 dark:text-emerald-400">
            ✓
          </span>
          <span className="text-emerald-900 dark:text-emerald-200">
            Pre-filled from {lookupSource.distributorName} ·{' '}
            <span className="font-mono text-xs">{lookupSource.pn}</span>
          </span>
          <span className="text-xs text-stone-500 dark:text-stone-400">
            — edit any field below, then click Save.
          </span>
        </div>
        <button
          type="button"
          onClick={onClear}
          className="text-xs text-stone-500 dark:text-stone-400 hover:text-red-600"
        >
          Clear
        </button>
      </div>
    );
  }

  if (dismissed) return null;

  const enabledRows =
    settings.data?.filter((r) => r.enabled && r.adapter_available) ?? [];
  const hasAnyEnabled = enabledRows.length > 0;

  if (settings.data && !hasAnyEnabled) {
    return (
      <div className="rounded-md border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-950/40 p-3 text-xs text-stone-600 dark:text-stone-400">
        Tip: enable a distributor in{' '}
        <span className="font-medium">Organization settings</span> to paste
        product URLs and auto-fill new parts.
      </div>
    );
  }

  const buildPayload = (extra: { preview: boolean }) => {
    const trimmed = input.trim();
    const looksLikeUrl = /^https?:\/\//i.test(trimmed);
    return looksLikeUrl
      ? { url: trimmed, ...extra }
      : { distributor_slug: bareSlug, pn: trimmed, ...extra };
  };

  const onLookup = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    lookup.mutate(buildPayload({ preview: true }), {
      onSuccess: (result) => {
        setPreview(result);
      },
    });
  };

  const onUseThisData = () => {
    if (!preview) return;
    onApply(preview);
    // Clear the local preview state — the banner will re-render with
    // the loaded indicator since the parent now has a lookupSource.
    setPreview(null);
    setInput('');
  };

  const onTryAgain = () => {
    setPreview(null);
    setInput('');
  };

  const apiError =
    lookup.error instanceof ApiError ? lookup.error : null;

  return (
    <div className="rounded-md border border-forge-200 dark:border-forge-900/60 bg-forge-50/60 dark:bg-forge-900/10 p-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-medium text-forge-800 dark:text-forge-300">
            Look up a part from a distributor
          </h3>
          <p className="mt-0.5 text-xs text-stone-600 dark:text-stone-400">
            Paste a product URL or part number; we'll fetch the data so you
            can review it before saving. You can edit any field afterwards
            in the part drawer.
          </p>
        </div>
        {!preview && (
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="text-stone-400 dark:text-stone-500 hover:text-stone-600 dark:hover:text-stone-300"
            aria-label="Dismiss"
          >
            ✕
          </button>
        )}
      </div>

      {!preview ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onLookup();
              }
            }}
            placeholder="https://www.lcsc.com/product-detail/…  or  C25804"
            className="min-w-[18rem] flex-1"
            disabled={lookup.isPending}
            autoComplete="off"
          />
          {!/^https?:\/\//i.test(input.trim()) && enabledRows.length > 1 && (
            <select
              value={bareSlug}
              onChange={(e) => setBareSlug(e.target.value)}
              className="rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-2 py-1.5 text-sm"
              disabled={lookup.isPending}
            >
              {enabledRows.map((r) => (
                <option key={r.distributor_slug} value={r.distributor_slug}>
                  {r.distributor_name}
                </option>
              ))}
            </select>
          )}
          <Button
            type="button"
            onClick={onLookup}
            disabled={lookup.isPending || !input.trim()}
          >
            {lookup.isPending ? (
              <>
                <Spinner className="mr-2 h-4 w-4" />
                Looking up…
              </>
            ) : (
              'Look up'
            )}
          </Button>
        </div>
      ) : (
        <PreviewCard
          preview={preview}
          onUseThisData={onUseThisData}
          onTryAgain={onTryAgain}
        />
      )}

      {apiError && (
        <p className="mt-2 text-xs text-red-600">
          {apiError.status === 404
            ? `${apiError.message} — double-check the URL or PN.`
            : apiError.status === 400
              ? apiError.message
              : apiError.status === 502
                ? "Couldn't reach the distributor right now. Try again in a minute, or fill in the form below manually."
                : apiError.message}
        </p>
      )}
    </div>
  );
}

function PreviewCard({
  preview,
  onUseThisData,
  onTryAgain,
}: {
  preview: LookupResult;
  onUseThisData: () => void;
  onTryAgain: () => void;
}) {
  // The server tells us when this PN already maps to an existing part.
  // We surface it as a yellow warning banner above the preview card so
  // the user can choose to fill blanks on the existing row, or open
  // the existing row instead. Without this banner, "Use this data"
  // silently heals + closes the modal — confusing UX.
  const matchedExisting =
    preview.existing_part_id != null && preview.existing_part_number;
  return (
    <div className="mt-3 space-y-2">
      {matchedExisting && (
        <div className="rounded-md border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20 px-3 py-2 text-sm">
          <div className="flex items-start gap-2">
            <span aria-hidden className="text-amber-600 dark:text-amber-500">
              ⚠
            </span>
            <div className="flex-1">
              <div className="font-medium text-amber-900 dark:text-amber-300">
                You already have this part as{' '}
                <Link
                  to={`/parts?part=${preview.existing_part_id}`}
                  className="font-mono underline hover:no-underline"
                >
                  {preview.existing_part_number}
                </Link>
                .
              </div>
              <div className="mt-0.5 text-xs text-amber-800 dark:text-amber-400">
                Clicking <em>Update existing</em> below will fill in any blank
                fields on{' '}
                <span className="font-mono">{preview.existing_part_number}</span>{' '}
                using LCSC's data — your typed values won't be overwritten.
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-baseline gap-2">
        <span className="rounded-full bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">
          Pulled from {preview.distributor_name ?? preview.distributor_slug}
        </span>
        <span className="font-mono text-xs text-stone-500 dark:text-stone-400">
          {preview.distributor_pn}
        </span>
        {preview.supplier_url && (
          <a
            href={preview.supplier_url}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto text-xs text-forge-700 dark:text-forge-400 hover:underline"
          >
            view on distributor →
          </a>
        )}
      </div>

      <div className="grid gap-x-4 gap-y-1 rounded border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 p-3 text-sm md:grid-cols-2">
        <PreviewField label="Mfg" value={preview.manufacturer} />
        <PreviewField label="Mfg PN" value={preview.mpn} mono />
        <PreviewField
          label="Description"
          value={preview.description}
          span={2}
        />
        <PreviewField label="Package" value={preview.package} />
        <PreviewField label="Category hint" value={preview.category_hint} />
        {preview.stock_qty != null && (
          <PreviewField label="Stock" value={preview.stock_qty.toLocaleString()} />
        )}
        {preview.moq != null && (
          <PreviewField label="MOQ" value={String(preview.moq)} />
        )}
        {preview.lead_time_days != null && (
          <PreviewField
            label="Lead time"
            value={`${preview.lead_time_days} days`}
          />
        )}
        {preview.package_form && (
          <PreviewField label="Pkg form" value={preview.package_form} />
        )}
        {preview.image_url && (
          <PreviewField
            label="Image"
            value={
              <a
                href={preview.image_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-forge-700 dark:text-forge-400 hover:underline"
              >
                preview ↗
              </a>
            }
          />
        )}
        {preview.datasheet_url && (
          <PreviewField
            label="Datasheet"
            value={
              <a
                href={preview.datasheet_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-forge-700 dark:text-forge-400 hover:underline"
              >
                open PDF ↗
              </a>
            }
          />
        )}
      </div>

      {preview.price_breaks.length > 0 && (
        <details className="rounded border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 p-2 text-sm">
          <summary className="cursor-pointer text-xs font-medium text-stone-600 dark:text-stone-400">
            Price breaks ({preview.price_breaks.length})
          </summary>
          <table className="mt-2 w-full text-xs">
            <thead className="text-stone-500 dark:text-stone-400">
              <tr>
                <th className="text-left">Qty</th>
                <th className="text-right">Unit price</th>
              </tr>
            </thead>
            <tbody>
              {preview.price_breaks.map((pb, i) => (
                <tr key={i}>
                  <td className="font-mono">{pb.qty.toLocaleString()}</td>
                  <td className="text-right font-mono">
                    {(pb.price_unit_cents / 100).toFixed(4)} {pb.currency}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {preview.parameters && Object.keys(preview.parameters).length > 0 && (
        <details className="rounded border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 p-2 text-sm">
          <summary className="cursor-pointer text-xs font-medium text-stone-600 dark:text-stone-400">
            Parameters ({Object.keys(preview.parameters).length})
          </summary>
          <dl className="mt-2 grid grid-cols-1 gap-x-4 text-xs md:grid-cols-2">
            {Object.entries(preview.parameters).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-2">
                <dt className="text-stone-500 dark:text-stone-400">{k}</dt>
                <dd className="font-mono text-stone-800 dark:text-stone-200">
                  {v}
                </dd>
              </div>
            ))}
          </dl>
        </details>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="secondary" onClick={onTryAgain}>
          Try another
        </Button>
        <Button type="button" onClick={onUseThisData}>
          {matchedExisting ? 'Update existing' : 'Use this data'}
        </Button>
      </div>
    </div>
  );
}

function PreviewField({
  label,
  value,
  span,
  mono,
}: {
  label: string;
  value: ReactNode;
  span?: number;
  mono?: boolean;
}) {
  if (value == null || value === '') return null;
  return (
    <div className={span === 2 ? 'md:col-span-2' : ''}>
      <span className="text-xs text-stone-500 dark:text-stone-400">{label}: </span>
      <span
        className={
          mono
            ? 'font-mono text-stone-800 dark:text-stone-200'
            : 'text-stone-800 dark:text-stone-200'
        }
      >
        {value}
      </span>
    </div>
  );
}
